/**
 * Frontend helper for the 409 REWORK_DECISION_REQUIRED contract.
 *
 * Backend SQL gate (oee_rework_criteria_met + the three time-entry functions
 * + handleEnsureBatchAtStation + reactivateArchivedBatchEvent) returns this
 * response when an operation would extend a time entry past me.end_ts at a
 * station without a rework decision:
 *
 *   HTTP 409
 *   { code: 'REWORK_DECISION_REQUIRED', station, completedAt, completedBy }
 *
 * Callers wrap their submit in `submitWithReworkPrompt(...)` which catches the
 * 409, opens the modal, and retries with is_rework set. Cancel from the modal
 * returns null — caller should abort.
 */

import { Observable, throwError, of } from 'rxjs'
import { catchError, switchMap } from 'rxjs/operators'
import { MatDialog } from '@angular/material/dialog'

import {
    ReworkDecisionModalComponent,
    ReworkDecisionModalData,
    ReworkDecisionModalResult
} from './rework-decision-modal.component'

export interface ReworkDecisionPayload {
    code: 'REWORK_DECISION_REQUIRED'
    station: string | null
    completedAt: string | null
    completedBy: string | null
}

/** True iff the error is the 409 envelope the SQL gate emits. */
export function isReworkDecisionRequired(error: unknown): error is { status: 409, error: ReworkDecisionPayload } {
    if (!error || typeof error !== 'object') return false
    const e = error as { status?: number, error?: { code?: string } }
    return e.status === 409 && e.error?.code === 'REWORK_DECISION_REQUIRED'
}

/** Open the rework decision modal. Resolves to { isRework } on submit, or undefined on cancel. */
export function openReworkDecisionModal(
    dialog: MatDialog,
    payload: ReworkDecisionPayload
): Observable<ReworkDecisionModalResult | undefined> {
    const ref = dialog.open<ReworkDecisionModalComponent, ReworkDecisionModalData, ReworkDecisionModalResult | undefined>(
        ReworkDecisionModalComponent,
        {
            width: '460px',
            maxWidth: '95vw',
            panelClass: 'oee-mobile-modal',
            // Required gate — no backdrop / esc dismiss without going through Cancel.
            disableClose: true,
            data: {
                station: payload.station,
                completedAt: payload.completedAt,
                completedBy: payload.completedBy
            }
        }
    )
    return ref.afterClosed()
}

/**
 * Wrap a submit call so it auto-prompts on 409. The factory is called with
 * the decision (null on first try, true|false after the modal). On cancel
 * the helper short-circuits to undefined — caller should treat undefined
 * as "operator aborted, do not retry."
 */
export function submitWithReworkPrompt<T>(
    dialog: MatDialog,
    submit: (isRework: boolean | null) => Observable<T>
): Observable<T | undefined> {
    return submit(null).pipe(
        catchError((error: unknown) => {
            if (!isReworkDecisionRequired(error)) {
                return throwError(() => error)
            }
            return openReworkDecisionModal(dialog, error.error).pipe(
                switchMap((result: ReworkDecisionModalResult | undefined) => {
                    if (!result) return of(undefined as T | undefined)
                    return submit(result.isRework)
                })
            )
        })
    )
}
